/**
 * @module components/TeamSection
 * @description Leadership team grid with expandable bios.
 *
 * @extracted-from client/src/App.js (lines ~3253–3338, Phase 7)
 */

import React, { useState } from 'react';
import { Linkedin, Plus, Minus } from 'lucide-react';
import TEAM_MEMBERS from '../../constants/teamMembers';
import { Eyebrow } from '../ui';

export default function TeamSection() {
  const [expandedLeadershipBio, setExpandedLeadershipBio] = useState(null);

  return (
    <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 mt-4 border-t border-white/[0.06]">
      <div className="text-center mb-12 md:mb-14 max-w-2xl mx-auto">
        <Eyebrow className="mb-4">
          People
        </Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient-insight tracking-tight leading-tight mb-4">
          Meet the team
        </h2>
        <p className="text-base text-critic-text-secondary font-normal leading-relaxed">
          Builders and advisors behind CriticAI.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
        {TEAM_MEMBERS.map((member) => (
          <article
            key={member.id}
            className="group flex flex-col rounded-2xl border border-white/[0.08] bg-critic-bg-card/25 backdrop-blur-xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.3)] transition-all duration-300 hover:border-white/[0.14] hover:bg-critic-bg-card/35"
          >
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block aspect-[4/5] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-apple-blue/60 focus-visible:ring-offset-2 focus-visible:ring-offset-critic-bg-deep"
              aria-label={`${member.name} — view LinkedIn profile`}
            >
              <img
                src={member.image}
                alt={member.imageAlt}
                loading="lazy"
                className="h-full w-full object-cover object-top scale-100 transition-transform duration-700 ease-out group-hover:scale-[1.03] contrast-[1.02] grayscale-[0.2] group-hover:grayscale-0"
              />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-70"
                aria-hidden
              />
            </a>
            <div className="flex flex-1 flex-col p-6 md:p-7 text-left">
              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="group/link block focus:outline-none"
              >
                <h3 className="font-display text-xl md:text-2xl font-semibold text-critic-text-primary tracking-tight group-hover/link:text-white transition-colors">
                  {member.name}
                </h3>
                <p className="mt-1.5 text-sm text-critic-text-secondary font-normal leading-snug">{member.role}</p>
              </a>
              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex w-fit items-center gap-1.5 text-xs font-medium text-critic-text-muted hover:text-apple-blue transition-colors duration-200"
                aria-label={`${member.name} on LinkedIn`}
              >
                <Linkedin className="w-3.5 h-3.5 opacity-80" />
                <span>LinkedIn</span>
              </a>
              <div className="mt-6 flex items-center">
                <button
                  type="button"
                  onClick={() => setExpandedLeadershipBio((prev) => (prev === member.id ? null : member.id))}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.1] bg-white/[0.06] text-critic-text-secondary transition-all duration-200 hover:border-white/[0.15] hover:bg-white/[0.1] hover:text-white"
                  aria-expanded={expandedLeadershipBio === member.id}
                  aria-label={expandedLeadershipBio === member.id ? 'Collapse bio' : 'Expand bio'}
                >
                  {expandedLeadershipBio === member.id ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {expandedLeadershipBio === member.id && (
              <div className="border-t border-white/[0.06] px-6 pb-6 pt-0 md:px-7 md:pb-7">
                <div className="mt-4 rounded-xl border border-white/[0.06] bg-black/25 p-5 backdrop-blur-sm space-y-3">
                  {member.bioParagraphs.map((paragraph, idx) => (
                    <p key={idx} className="text-sm text-critic-text-secondary font-normal leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
